import mergeDeepRight from 'ramda/src/mergeDeepRight';
const hasOwnProperty = Object.prototype.hasOwnProperty;
export { settlePromises } from './promise/settle';
export function getCurrentTime() {
  return new Date().getTime();
}
export function generateRandomGuid4() {
  /* eslint-disable no-bitwise */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
  /* eslint-enable no-bitwise */
}
export function noop() {}
export function identity(a) {
  return a;
}

/**
 * Extend deep objects
 * @param object {Object}
 * @returns {Object}
 * @description Deep merge objects, works like lodash merge.
 */
export function extendDeep() {
  for (var _len = arguments.length, objects = new Array(_len), _key = 0; _key < _len; _key++) {
    objects[_key] = arguments[_key];
  }
  return objects.filter(Boolean).reduce(mergeDeepRight);
}

/**
 * Gets the value at path of object.
 * If the resolved value is undefined, the defaultValue is returned in its place.
 * @param object
 * @param path
 * @param defaultValue
 * @returns {*}
 */
export function get(object, path, defaultValue) {
  if (!object || !path) {
    return defaultValue;
  }
  let paths = Array.isArray(path) ? path : path.split(/[.[\]]/);
  paths = paths.filter(function (path) {
    return path;
  });
  const result = paths.reduce(function (prevValue, currentNamespace) {
    if (prevValue && hasOwnProperty.call(prevValue, currentNamespace)) {
      return prevValue[currentNamespace];
    }
    return undefined;
  }, object);
  if (typeof result === 'undefined') {
    return defaultValue;
  }
  return result;
}
export function bytesToSize(bytes) {
  if (bytes === 0) {
    return '0 Byte';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toPrecision(3)} ${sizes[i]}`;
}

/**
 * Return function with preset arguments
 * @param func
 * @param restArguments
 * @returns {function(...[*]=): *}
 */
export function partial(func) {
  for (var _len2 = arguments.length, restArguments = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    restArguments[_key2 - 1] = arguments[_key2];
  }
  return function () {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    return func.apply(this, restArguments.concat(args));
  };
}

/**
 * Return parameter value from search string
 * @param searchString {String}
 * @param name {String}
 * @returns {String}
 */
export function getParameterByName(searchString, name) {
  name = name.replace(/\[/, '\\[').replace(/\]/, '\\]');
  const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
  const results = regex.exec(searchString);
  if (!results) {
    return '';
  }
  return decodeURIComponent(results[1].replace(/\+/g, ' '));
}

/**
 * Returns parsed information from siteMediaToken
 * @param token {String}
 * @returns {Object}
 */
export function parseSiteMediaToken(token) {
  if (!token || typeof token !== 'string') {
    throw new Error('Token should be a string');
  }
  const parsed = simpleParseJwt(token);
  const {
    metasiteId: metaSiteId,
    userId,
    roles
  } = parsed;
  return {
    metaSiteId,
    userId,
    roles
  };
}
export function isSameSite(token1, token2) {
  try {
    const siteId1 = parseSiteMediaToken(token1).metaSiteId;
    const siteId2 = parseSiteMediaToken(token2).metaSiteId;
    return siteId1 === siteId2;
  } catch (reason) {
    console.error(reason);
    return false;
  }
}
function simpleParseJwt(token) {
  try {
    return JSON.parse(JSON.parse(atob(token.split('.')[2])).data);
  } catch (e) {
    throw new Error('Can not parse token');
  }
}

/**
 * Return camel case string from snake case string
 * @param string {String}
 * @returns {String}
 */
export function snakeToCamel(string) {
  string = string[0].toLowerCase() + string.slice(1);
  return string.replace(/[_]+$/g, '').replace(/([^_]?)[_]+([^_])/g, function ($0, $1, $2) {
    if (!$1) {
      return $2.toLowerCase();
    }
    return $1.toLowerCase() + $2.toUpperCase();
  });
}
export const encodeParams = data => Object.keys(data).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`).join('&').replace(/%20/g, '+');
export const decodeParams = str => str.replace(/\+/g, '%20').split('&').reduce((acc, pair) => {
  const [key, value] = pair.split('=');
  acc[decodeURIComponent(key)] = decodeURIComponent(value);
  return acc;
}, {});
export function removeUndefinedValues(data) {
  const dataWithoutUndefined = {};
  Object.keys(data).forEach(function (key) {
    const value = data[key];
    if (typeof value === 'undefined') {
      return;
    }
    dataWithoutUndefined[key] = value;
  });
  return dataWithoutUndefined;
}
export function splitTags(tags) {
  if (!tags) {
    return [];
  }
  if (Array.isArray(tags)) {
    return tags;
  }
  return tags.split(/\s*,\s*/);
}
export function normalizeUri(uri) {
  if (typeof uri !== 'string') {
    throw new Error('URI is not a string');
  }
  return uri.replace(/([a-z\-_0-9]+)\/\//gi, '$1/');
}

/**
 * Return snake case string from camel case string
 * @param string {String}
 * @returns {String}
 */
export function camelToSnake(string) {
  return string.replace(/([a-z]?)([A-Z])/g, function ($0, $1, $2) {
    if (!$1) {
      return $0.toLowerCase();
    }
    return `${$1}_${$2.toLowerCase()}`;
  });
}

/**
 * sortBy like lodash _.sortBy
 * @param {Object[]} arrayOfObjects
 * @param {String} key
 * @param {String} [sortOrder='asc']
 * @returns {Object[]} sorted array
 */
export function sortBy(arrayOfObjects, key, sortOrder) {
  if (!Array.isArray(arrayOfObjects)) {
    return [];
  }
  return arrayOfObjects.slice().sort(function (a, b) {
    let order = 0;
    if (a[key] < b[key]) {
      order = -1;
    } else if (a[key] > b[key]) {
      order = 1;
    }
    if (sortOrder === 'desc') {
      return -order;
    }
    return order;
  });
}

/**
 * getThumbnailsUrlsFromList
 * @param {Array} sourceSizes
 * @param {Object} requiredSizes
 * @returns {Object.<thumbnailUrl, previewUrl>}
 */
export function getThumbnailsUrlsFromList(sourceSizes, requiredSizes) {
  if (!sourceSizes.length) {
    return {};
  }
  function canFill(sizes, sizesToTest) {
    return sizesToTest.width >= sizes.width && sizesToTest.height >= sizes.height;
  }
  function canFit(sizes, sizesToTest) {
    return sizesToTest.width >= sizes.width || sizesToTest.height >= sizes.height;
  }
  const sourceSizesSorted = sortBy(sourceSizes, 'width');
  return Object.keys(requiredSizes).reduce(function (urls, sizeName) {
    const requiredSize = requiredSizes[sizeName];
    const currentSize = sourceSizesSorted.find(function (size) {
      return requiredSize.mode === 'fill' ? canFill(requiredSize, size) : canFit(requiredSize, size);
    });
    urls[`${sizeName}Url`] = currentSize ? currentSize.url : sourceSizesSorted[sourceSizesSorted.length - 1].url;
    return urls;
  }, {});
}

/**
 * replaceHtml
 * @param {String} htmlString
 * @returns {String}
 */
export function replaceHtml(htmlString) {
  return (htmlString || '').replace(/<\/?[a-z]+.*?>/gi, '');
}

/**
 * toTimeStamp
 * @param {String} date
 * @returns {Number} time stamp in seconds
 */
export function toTimeStamp(date) {
  return !date ? null : Math.floor(new Date(date).getTime() / 1000);
}

/**
 * getOriginId
 * @param {String} id
 * @returns {String} origin id without suffixes
 */
export function getOriginId(id) {
  return id.split(':')[0];
}

/**
 * uniqueArray
 * @param {Array<T>} arr
 * @returns {Array<T>} array with all unique values
 */
export function uniqueArray(arr) {
  return arr.filter((elem, pos, arr) => arr.indexOf(elem) === pos);
}

/**
 * Creates new middleware
 * @param {Array<Function>} middlewares
 */
export function createMiddleware(middlewares) {
  let _middlewares = middlewares;
  function emit(data, i) {
    if (i === void 0) {
      i = 0;
    }
    if (_middlewares[i]) {
      _middlewares[i](data, newData => emit(newData, i + 1));
    }
  }
  function setMiddlewares(middlewares) {
    _middlewares = middlewares;
  }
  function getMiddlewares() {
    return _middlewares;
  }
  return {
    emit,
    getMiddlewares,
    setMiddlewares
  };
}
function minMaxFinder(method, arr, predicate) {
  let mappedArr;
  if (predicate) {
    mappedArr = arr.map(predicate);
    return arr[mappedArr.indexOf(Math[method].apply(null, mappedArr))];
  }
  return Math[method].apply(this, arr);
}
export const min = partial(minMaxFinder, 'min');
export const max = partial(minMaxFinder, 'max');

// from santa-editor
export const getCookie = name => {
  const re = new RegExp(`(?:^|\\W)${name}\s*=([^;]*)`);
  const m = window.document.cookie.match(re);
  return m ? m[1].trim() : null;
};